import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import { apiCall } from "/WebInterface/new-ui/assets/js/utils/api/apis.js";
import Modal from "/WebInterface/new-ui/modules/modal.js";
import {
  connectionLostTemplate,
  electronUserInfoTemplate,
} from "/WebInterface/new-ui/upload/electron-app/template.js";
import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";
import Tooltip from "/WebInterface/new-ui/modules/tooltip/tooltip.js";
import { initShareHistoryModal } from "/WebInterface/new-ui/upload/electron-app/electron-share-history/electron-share-history.js";
import uploadingQueue from "/WebInterface/new-ui/upload/js/uploading-queue.js";
import { beforeUnloadEvent } from "/WebInterface/new-ui/upload/js/common.js";

// To invoke parent methods
function invoke(method, params) {
  const _parent =
    typeof self.window[method] === "function" ? self.window : parent.window;
  if (_parent[method]) {
    return _parent[method].apply(this, params);
  }
}

let failedSessionCalls = 0;
let dialog;
let userInfoCallInFlight = false;
let userInfoCallTimeoutId = null;

const scheduleNextUserInfoCall = () => {
  if (userInfoCallTimeoutId !== null) {
    clearTimeout(userInfoCallTimeoutId);
  }
  userInfoCallTimeoutId = setTimeout(() => {
    userInfoCallTimeoutId = null;
    makeUserInfoCall();
  }, 10000);
};

const makeUserInfoCall = async () => {
  if (userInfoCallInFlight) {
    return;
  }
  userInfoCallInFlight = true;
  try {
    await apiCall("getUserInfo");

    failedSessionCalls = 0;

    if (dialog?.visible()) {
      dialog?.hide();
    }
  } catch (error) {
    if (!dialog?.visible()) {
      dialog?.show();
    }

    failedSessionCalls += 1;
  } finally {
    if (failedSessionCalls === 3) {
      if (userInfoCallTimeoutId !== null) {
        clearTimeout(userInfoCallTimeoutId);
        userInfoCallTimeoutId = null;
      }
      const ipcRenderer = invoke("getIPCRenderer");
      if (ipcRenderer) {
        ipcRenderer.send("user-logged-out");
      }

      userInfoCallInFlight = false;
      return;
    }

    _$("#try-now-button")?.el()?.removeAttribute("disabled");
    userInfoCallInFlight = false;
    scheduleNextUserInfoCall();
  }
};

const configureElectronApp = () => {
  _$("body").addClass("electron-app").attr("data-electron-upload-host", "true");

  _$(".electron-app-remove").remove();
  _$(".electron-app-hidden").addClass("is-hidden");
  _$(".electron-app-show").removeClass("is-hidden");

  const userInfo = invoke("getUserInfo") || {};
  const path = invoke("currentPath") || "";

  const username = userInfo.username || "";
  const { hostname, port } = location;

  _$("#electronAppInfoButton").attr(
    "data-tooltip",
    electronUserInfoTemplate({
      serverText: t("server"),
      userText: t("user"),
      pathText: t("path"),
      servername: `${hostname}:${port}`,
      username,
      path,
    })
  );

  const tooltip = new Tooltip();
  const tooltipElements = _$(".upload-action[data-tooltip]").sel();

  tooltipElements.forEach((element) => {
    const position = _$(element).attr("tooltip-position") || "bottom";
    const isElectronAppInfoButton = element.id === "electronAppInfoButton";
    const closeTrigger = isElectronAppInfoButton ? "click" : "hover";
    tooltip.addTooltip({
      tooltipText: t(_$(element).attr("data-tooltip")),
      element,
      position,
      closeTrigger,
      intrinsicSizeMeasure: isElectronAppInfoButton,
    });
  });

  _$("#electronAppReloadButton").on("click", () => {
    window.removeEventListener("beforeunload", beforeUnloadEvent);
    queueMicrotask(() => {
      location.reload();
    });
  });

  _$("#electronAppLogoutButton").on("click", () => {
    if (uploadingQueue.fileUploadingInProgress) {
      const confirmation = window.confirm("Are you sure you want to logout?");
      if (confirmation) {
        invoke("logout");
      }
    } else {
      invoke("logout");
    }
  });

  _$("#electronAppQuitButton").on("click", () => {
    const quitApp = () => {
      const ipcRenderer = invoke("getIPCRenderer");
      if (ipcRenderer) {
        ipcRenderer.send("quit-app");
      }
    };

    if (uploadingQueue.fileUploadingInProgress) {
      const confirmation = window.confirm(
        "Are you sure you want to quit the app?"
      );
      if (confirmation) {
        quitApp();
      }
    } else {
      quitApp();
    }
  });

  _$("#electronAppShareHistoryButton").on("click", () => {
    initShareHistoryModal();
  });
};

const deconfigureElectronApp = () => {
  _$(".electron-app-show").remove();
};

// only for electron app
_$("body").ready(() => {
  const isElectronUploadOnlyApp = invoke("isElectron");

  if (!isElectronUploadOnlyApp) {
    deconfigureElectronApp();
    return;
  }

  dialog = new Modal({
    body: connectionLostTemplate({
      tryNowText: t("tryNow"),
      tryingToReconnectElectronAppText: t("tryingToReconnectElectronApp"),
    }),
    showHeader: false,
    closable: false,
    hasFooter: false,
    extraClasses: ["connection-lost-modal", "p-0"],
    buttons: [],
  });

  _$("#try-now-button").on("click", () => {
    _$("#try-now-button").attr("disabled", true);
    if (userInfoCallTimeoutId !== null) {
      clearTimeout(userInfoCallTimeoutId);
      userInfoCallTimeoutId = null;
    }
    makeUserInfoCall();
  });

  makeUserInfoCall();
  configureElectronApp();
});
